import React from 'react';
import { Box } from '@mui/material';
import {
  AccessTime,
  CheckCircle,
  PlayArrow,
  SportsScore,
  SportsSoccer,
  SwapHoriz,
  Timer,
} from '@mui/icons-material';
import type { MatchEventType } from '@/types';

export type EventMetaCode = 'match_start' | 'second_yellow_red' | 'halftime' | 'match_end' | 'added_time';

export const defaultEventTypeKey = 'goal';

const cardIcon = (color: string) => (
  <Box
    component="span"
    sx={{
      width: 12,
      height: 16,
      borderRadius: 0.5,
      bgcolor: color,
      border: '1px solid',
      borderColor: 'divider',
      display: 'inline-block',
    }}
  />
);

export const eventTypeOptions: { key: string; value: MatchEventType; label: string; icon: React.ReactNode; metaCode?: EventMetaCode }[] = [
  { key: 'match_start', value: 'timeout', label: 'Начало матча', icon: <PlayArrow sx={{ color: '#4caf50' }} />, metaCode: 'match_start' },
  { key: 'goal', value: 'goal', label: 'Гол', icon: <SportsSoccer sx={{ color: '#4caf50' }} /> },
  { key: 'point', value: 'point', label: 'Очко', icon: <SportsScore sx={{ color: '#4caf50' }} /> },
  { key: 'card_yellow', value: 'card_yellow', label: 'Желтая карточка', icon: cardIcon('#ffeb3b') },
  { key: 'second_yellow_red', value: 'card_red', label: 'Вторая желтая карточка - удаление', icon: cardIcon('#f44336'), metaCode: 'second_yellow_red' },
  { key: 'card_red', value: 'card_red', label: 'Красная карточка - удаление', icon: cardIcon('#f44336') },
  { key: 'foul', value: 'foul', label: 'Фол', icon: <Timer sx={{ color: '#ff9800' }} /> },
  { key: 'penalty', value: 'penalty', label: 'Удаление', icon: <Timer sx={{ color: '#f44336' }} /> },
  { key: 'substitution', value: 'substitution', label: 'Замена', icon: <SwapHoriz sx={{ color: '#2196f3' }} /> },
  { key: 'halftime', value: 'timeout', label: 'Перерыв', icon: <AccessTime sx={{ color: '#9e9e9e' }} />, metaCode: 'halftime' },
  { key: 'added_time', value: 'timeout', label: 'Добавленное время', icon: <AccessTime sx={{ color: '#9e9e9e' }} />, metaCode: 'added_time' },
  { key: 'match_end', value: 'timeout', label: 'Окончание матча', icon: <CheckCircle sx={{ color: '#4caf50' }} />, metaCode: 'match_end' },
];

const optionFor = (type: string, meta?: Record<string, unknown>) => {
  const metaCode = typeof meta?.eventCode === 'string' ? meta.eventCode : '';
  if (metaCode) {
    const byCode = eventTypeOptions.find((option) => option.metaCode === metaCode);
    if (byCode) return byCode;
  }
  return eventTypeOptions.find((option) => option.value === type && !option.metaCode);
};

export const matchEventIcon = (type: string, meta?: Record<string, unknown>): React.ReactNode =>
  optionFor(type, meta)?.icon ?? <AccessTime />;

export const matchEventLabel = (type: string, meta?: Record<string, unknown>, fallback?: string): string =>
  optionFor(type, meta)?.label ?? fallback ?? type;

export const getEventOptionByKey = (key: string) =>
  eventTypeOptions.find((option) => option.key === key) ?? eventTypeOptions[0];

export const getEventTypeKeyFromEvent = (type: string, meta?: Record<string, unknown>): string =>
  optionFor(type, meta)?.key ?? defaultEventTypeKey;
